// Display helpers for surge pay (GRAVY-10443). Money is cents on the wire and
// dollars in the form.

export const formatCents = (cents: number): string =>
  `$${(cents / 100).toFixed(2)}`;

/** "1 hr", "6 hrs" — the lead-time dropdown's labels. */
export const leadTimeLabel = (minutes: number): string => {
  if (minutes % 60 !== 0) return `${minutes} min`;
  const hours = minutes / 60;
  return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
};

/**
 * What a surge amount field keeps as the admin types (GRAVY-10860).
 *
 * The old rule was `replace(/[^\d.]/g, '')`, which DELETED a comma rather than
 * reading it: typing "0,5" — fifty cents with a decimal comma — left "05", and
 * the form then charged $5.00, ten times the intended rate and exactly the
 * platform maximum, with Save enabled. A comma in a money field is always a
 * decimal separator at these amounts (the ceiling is $5.00, so there are no
 * thousands to group), so it becomes a dot instead of vanishing.
 *
 * Only the first dot is kept, so "1..5" and "1.5.0" cannot produce something
 * `Number()` would read as NaN and the caller would have to guess about.
 */
export const sanitizeAmountInput = (raw: string): string => {
  const [whole, ...rest] = raw
    .replace(/,/g, '.')
    .replace(/[^\d.]/g, '')
    .split('.');
  return rest.length > 0 ? `${whole}.${rest.join('')}` : whole;
};

/**
 * A typed dollar amount to cents, or null when it is not a usable amount.
 * Accepts "1.5", "1.50" and "$1.50"; refuses more than two decimals rather
 * than silently rounding what the admin typed.
 *
 * The comma is normalised here too, not stripped (GRAVY-10860) — this is also
 * reached with values the input handler never saw, such as a schedule sent
 * straight to `dollarsToCents`.
 */
export const dollarsToCents = (input: string): number | null => {
  const value = input.replace(/[$\s]/g, '').replace(/,/g, '.');
  if (!/^\d+(\.\d{0,2})?$/.test(value)) return null;
  return Math.round(Number(value) * 100);
};

export const centsToDollars = (cents: number): string =>
  (cents / 100).toFixed(2);

/** "6 hours", "45 minutes" — how long until a tier fires. */
export const untilLabel = (iso: string, now: number = Date.now()): string => {
  const minutes = Math.max(
    0,
    Math.round((new Date(iso).getTime() - now) / 60_000),
  );
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

/*
 * What a raise actually reaches — Product's Decision 2, 2026-09-30.
 *
 * Pay is stored once, on the JOB. A job can hold several shifts, so raising pay
 * for Friday raises it for Saturday and Sunday too, including shifts already
 * fully staffed. That is settled behaviour (Product, 16 September); what was
 * wrong is that four screens promised "this shift only" — the opposite.
 *
 * Product chose "name the consequence" where money is about to move (the save
 * confirmation and the job banner) and the simpler "this job" elsewhere. They
 * did not supply an exact sentence, so these are the decision document's own
 * words.
 *
 * A one-shift job keeps "this shift": it is accurate, it is what ops call it,
 * and "This job has 1 shift" reads like a bug.
 */

/** Manage surge pay / Override amount — scope, without the arithmetic. */
export const surgeScopeNote = (shiftsCount: number): string =>
  shiftsCount > 1
    ? 'This applies to this job. It won’t change the client’s surge schedule or future shifts.'
    : 'This applies to this shift only. It won’t change the client’s surge schedule or future shifts.';

/** Manage surge pay's off switch — the same scope as the note beneath it. */
export const surgeOffNote = (shiftsCount: number): string =>
  shiftsCount > 1
    ? 'Off — this job won’t get automatic pay increases.'
    : 'Off — this shift won’t get automatic pay increases.';

/** Save confirmation / job banner — say what it will cost before they commit. */
export const surgeReachNote = (shiftsCount: number): string =>
  shiftsCount > 1
    ? `This job has ${shiftsCount} shifts. The new rate applies to all of them.`
    : 'The new rate applies to this shift.';
